import { chatEvents, roomEvents } from "@closerplatform/closer-sdk";
import { WidgetFormSubmissionWithWidget } from "@closerplatform/spinner-openapi";
import { ChatItemGroup } from "@fidget/common/models/chat/chatItemGroup";
import {
  ChatItemStatus,
  ContactEnquiryChatItem,
  DateSeparatorChatItem,
  IAttachment,
  IChatItem,
  IGroupableItem,
  IMessageItem,
  isGroupableItem,
  isUpdateableChatItem,
  MessageWithFormChatItem,
  UserTextChatItem
} from "@fidget/common/models/chat/chatItems";
import { IUserService } from "@fidget/common/services/userService";
import { logger } from "@fidget/common/utils";
import { createPendingId, createUndeliveredId } from "@fidget/common/utils/chatStoreUtils";
import { getDateSeparatorString } from "@fidget/common/utils/timeFormatter";
import { head, last, remove, sortedIndexBy, tail, toArray } from "lodash";
import { action, computed, observable } from "mobx";
import { ArtichokeEventConverter } from "../models/chat/eventConverters";
import { IForeSpinnerService } from "../services/spinnerService";
import { ChatInputBlocker, FormChatInputBlocker } from "./chatInputBlocker";
import Received = chatEvents.Received;
import RoomEvent = roomEvents.RoomEvent;
import {
  extractDynamicSuggestionsFromContext,
  extractDynamicSuggestionsFromContextOld
} from "../components/chat/richMessages/components/dynamicSuggestions/utils";

export class ChatStore {
  private readonly artichokeEventConverter: ArtichokeEventConverter;
  private readonly chatInputBlocker: ChatInputBlocker;

  @observable private readonly sortedHistory: IChatItem[] = [];
  @observable private readonly sortedPending: IMessageItem[] = [];
  @observable private readonly sortedUndelivered: IMessageItem[] = [];
  @observable private groupedHistory: IChatItem[] = [];
  @observable public dynamicSuggestions: string[] = [];
  public pendingMessages: (roomEvents.MessageSent | roomEvents.CustomMessageSent)[] = [];

  @observable public shouldBlockInput = false;

  constructor(
    private readonly userService: IUserService,
    private readonly spinnerService: IForeSpinnerService
  ) {
    this.artichokeEventConverter = new ArtichokeEventConverter(userService, spinnerService);
    this.chatInputBlocker = new FormChatInputBlocker();
  }

  @computed
  public get history(): ReadonlyArray<IChatItem> {
    return this.groupedHistory
      .concat(this.getPendingItemsGroup() || [])
      .concat(this.getUndeliveredItems() || []);
  }

  public get newestTimestamp(): number | undefined {
    return this.sortedHistory.slice(-1)[0]?.timestamp;
  }
  public get oldestTimestamp(): number | undefined {
    return this.sortedHistory[0]?.timestamp;
  }

  public get length(): number {
    return this.sortedHistory.length + this.sortedPending.length + this.sortedUndelivered.length;
  }

  public getItemById(id: string): IChatItem | undefined {
    return this.sortedHistory.find((item) => item.id === id);
  }

  public isUserInputBlocked(): boolean {
    return this.chatInputBlocker.checkShouldBlockInput(this.sortedHistory);
  }

  @action
  public async addArtichokeEvents(events: RoomEvent[]) {
    const firstHistoryEvent = (this.sortedHistory.length > 0 && this.sortedHistory[0]) || undefined;
    if (
      firstHistoryEvent &&
      DateSeparatorChatItem.isDateSeparatorChatItem(firstHistoryEvent) &&
      getDateSeparatorString(events[events.length - 1]!.timestamp) === firstHistoryEvent.id
    ) {
      this.sortedHistory.shift();
    }

    const chatItems = await Promise.all(
      events.map((event) => this.artichokeEventConverter.toChatItem(event))
    );
    logger.debug("adding artichoke events", chatItems);
    this.addChatItems(chatItems);
    this.shouldBlockInput = this.chatInputBlocker.checkShouldBlockInput(this.sortedHistory);
  }

  @action
  public addArtichokeEvent(event: RoomEvent) {
    return this.addArtichokeEvents([event]);
  }

  @action
  public addCustomMessage(message: IChatItem) {
    logger.debug("adding custom message", message);
    this.addChatItem(message);
  }

  @action
  public createPendingMessage(message: {
    text: string;
    attachments?: ArrayLike<IAttachment>;
  }): IMessageItem {
    const timestamp = Date.now();
    const id = createPendingId(timestamp);
    const user = this.userService.getCurrentUser();
    const item = new UserTextChatItem(
      id,
      timestamp,
      message.text,
      user,
      ChatItemStatus.SENDING,
      toArray(message.attachments)
    );
    logger.debug("adding pending message", item);
    this.insertChatItem(this.sortedPending, item);
    return item;
  }

  @action
  public pendingMessageSent(pendingMessageId: string, sentMessage: Received) {
    const pending = head(remove(this.sortedPending, (value) => value.id === pendingMessageId));
    if (pending) {
      const sent = sentMessage.message;
      const chatItem = new UserTextChatItem(
        sent.id,
        sent.timestamp,
        pending.text,
        pending.user,
        ChatItemStatus.SENT,
        pending.attachments
      );
      logger.debug("adding sent message", chatItem);
      this.addChatItem(chatItem);
    }
  }

  @action
  public pendingMessageFailed(pendingMessageId: string) {
    const pending = head(remove(this.sortedPending, (value) => value.id === pendingMessageId));
    if (pending) {
      const id = createUndeliveredId(pendingMessageId);
      const chatItem = new UserTextChatItem(
        id,
        pending.timestamp,
        pending.text,
        pending.user,
        ChatItemStatus.UNDELIVERED,
        pending.attachments
      );
      this.insertChatItem(this.sortedUndelivered, chatItem);
    }
  }

  @action
  public allPendingFailed() {
    this.sortedPending.forEach((pending) => {
      this.pendingMessageFailed(pending.id);
    });
  }

  @action
  public setReadUntilMark(userId: string, timestamp: number) {
    this.sortedHistory.forEach((item) => {
      if (
        UserTextChatItem.isUserTextChatItem(item) &&
        item.user.id === userId &&
        item.timestamp <= timestamp
      ) {
        item.status = ChatItemStatus.READ;
      }
    });
    this.groupHistory();
  }

  @action
  public setSubmission(messageId: string, submission: WidgetFormSubmissionWithWidget): void {
    const item = this.getItemById(messageId);
    if (item && MessageWithFormChatItem.isMessageWithFormChatItem(item)) {
      item.setSubmission(submission);
    }
    this.shouldBlockInput = this.chatInputBlocker.checkShouldBlockInput(this.sortedHistory);
  }

  @action
  public async reloadSubmission(formId: string): Promise<void> {
    const item: MessageWithFormChatItem | undefined = this.sortedHistory.find(
      (i) => MessageWithFormChatItem.isMessageWithFormChatItem(i) && i.formId === formId
    ) as MessageWithFormChatItem | undefined;
    if (item) {
      return this.spinnerService
        .getWidgetForm(formId)
        .then((submission) => item.setSubmission(submission))
        .then(() => {
          this.shouldBlockInput = this.chatInputBlocker.checkShouldBlockInput(this.sortedHistory);
        })
        .catch((e) => logger.error({ message: "Couldn't reload submission", error: e }));
    }
  }

  @action
  public updateMessage(messageId: string, data: any) {
    const item = this.getItemById(messageId);
    if (item && isUpdateableChatItem(item)) {
      item.updateFromEvent(data);
    }
  }

  @action
  public removeContactEnquiryItem() {
    remove(this.sortedHistory, (item) => item.tag === ContactEnquiryChatItem.tag);
  }

  @action
  public removeUndelivered(messageId: string) {
    const searchId = createUndeliveredId(messageId);
    remove(this.sortedUndelivered, (value) => value.id === searchId);
  }

  @action
  public resendUndelivered(messageId: string) {
    const searchId = createUndeliveredId(messageId);
    const message = head(remove(this.sortedUndelivered, (value) => value.id === searchId));
    if (message) {
      return this.createPendingMessage({ text: message.text, attachments: message.attachments });
    }
    return undefined;
  }

  private addDateSeparator(chatItem: IChatItem) {
    const dateSeparator = this.getDateSeparatorForChatItem(chatItem);
    if (dateSeparator) {
      this.insertChatItem(this.sortedHistory, dateSeparator);
    }
  }

  private getDateSeparatorForChatItem(chatItem: IChatItem): DateSeparatorChatItem | undefined {
    if (!chatItem.timestamp) {
      return;
    }
    const formattedDate = getDateSeparatorString(chatItem.timestamp);
    if (this.sortedHistory.some((item) => item.id === formattedDate)) {
      return;
    }

    return new DateSeparatorChatItem(formattedDate, chatItem.timestamp);
  }

  private addChatItems(items: IChatItem[]) {
    items.forEach((item) => {
      this.insertChatItem(this.sortedHistory, item);
      this.addDateSeparator(item);
    });
    this.groupHistory();
  }

  private addChatItem(item: IChatItem) {
    return this.addChatItems([item]);
  }

  private insertChatItem(items: IChatItem[], chatItem: IChatItem) {
    if (items.some((item) => item.id === chatItem.id)) {
      logger.warn(`insertChatItem ignoring duplicate item with id ${chatItem.id}`);
      return;
    }
    items.splice(sortedIndexBy(items, chatItem, "timestamp"), 0, chatItem);
  }

  private groupHistory() {
    this.groupedHistory = this.sortedHistory.reduce((acc: IChatItem[], item: IChatItem) => {
      if (isGroupableItem(item)) {
        const lastItem = last(acc);
        const added = lastItem && ChatItemGroup.isChatItemGroup(lastItem) && lastItem.add(item);
        if (!added) {
          acc.push(new ChatItemGroup(item));
        }
      } else {
        acc.push(item);
      }
      return acc;
    }, []);

    const lastChatItemGroup = this.groupedHistory.at(-1) as
      | ChatItemGroup<IGroupableItem>
      | undefined;
    const lastUserTextChatItem = lastChatItemGroup?.sortedItems?.at(-1) as
      | UserTextChatItem
      | undefined;
    if (lastUserTextChatItem?.context) {
      const res = extractDynamicSuggestionsFromContext(lastUserTextChatItem.context);
      if (res) {
        this.dynamicSuggestions = res;
      } else {
        const resOld = extractDynamicSuggestionsFromContextOld(lastUserTextChatItem.context);
        if (resOld) {
          this.dynamicSuggestions = resOld;
        } else {
          this.dynamicSuggestions = [];
        }
      }
    } else {
      this.dynamicSuggestions = [];
    }
  }

  private getPendingItemsGroup(): ChatItemGroup<IMessageItem> | undefined {
    const firstItem = head(this.sortedPending);
    if (firstItem) {
      const group = new ChatItemGroup(firstItem, Number.MAX_SAFE_INTEGER);
      tail(this.sortedPending).forEach((item) => group.add(item));
      return group;
    }
    return undefined;
  }

  private getUndeliveredItems(): readonly IChatItem[] | undefined {
    return this.sortedUndelivered.map((item) => {
      return new ChatItemGroup(item);
    });
  }
}
